import { api } from '@/apis';
import { log } from '@/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Polygon } from 'geojson';

type Group = {
  code: string;
  name: string;
  type?: string;
  parentCodes?: string[];
  ancestorCodes?: string[];
  aliasTypes?: string[];
  boundary?: Polygon;
  hidden?: boolean;
};

export function useGroups({
  types,
  ancestors,
  hidden = false,
}: { types?: string[]; ancestors?: string[]; hidden?: boolean } = {}) {
  const queryKey = ['groups', types, ancestors, hidden];
  const queryClient = useQueryClient();
  const query = useQuery<Group[]>({
    queryKey,
    queryFn: ({ signal }) => {
      const json = [
        types && { $match: { type: { $in: types } } },
        { $match: { hidden: hidden ? true : { $ne: true } } },
        {
          $graphLookup: {
            from: 'groups',
            startWith: '$parentCodes',
            connectFromField: 'parentCodes',
            connectToField: 'code',
            as: 'ancestorCodes',
          },
        },
        {
          $project: {
            _id: false,
            code: true,
            name: true,
            type: true,
            parentCodes: true,
            boundary: true,
            ancestorCodes: {
              $map: {
                input: '$ancestorCodes',
                as: 'group',
                in: '$$group.code',
              },
            },
            aliasTypes: {
              $map: {
                input: {
                  $filter: {
                    input: { $objectToArray: '$aliases' },
                    as: 'entry',
                    cond: { $ne: ['$$entry', []] },
                  },
                },
                as: 'alias',
                in: '$$alias.k',
              },
            },
            hidden: true,
          },
        },
        ancestors && {
          $match: {
            $or: [
              { ancestorCodes: { $in: ancestors } },
              { code: { $in: ancestors } },
            ],
          },
        },
        { $sort: { name: 1 } },
      ].filter(Boolean);

      log('Read', 'Groups', { types, ancestors });

      return api.post('pipeline/groups', { json, signal }).json();
    },
    placeholderData: [],
    staleTime: 1000 * 60 * 60 * 12,
  });

  return {
    query,
    cancel: () => queryClient.cancelQueries({ queryKey }),
  };
}
